<script setup lang="ts">
import type { JobPickingStatus } from '@/components/chips/PickingStatusChip.vue';

import KBtn from '@/components/btns/KBtn.vue';
import PickingStatusChip from '@/components/chips/PickingStatusChip.vue';
import CloseWindowIconBtn from '@/components/iconBtns/CloseWindowIconBtn.vue';
import EmDashPlaceholder from '@/components/placeholders/EmDashPlaceholder.vue';
import KTooltip from '@/components/tooltips/KTooltip.vue';
import JobEditActionsVMenu from '@/components/vMenus/JobEditActionsVMenu.vue';

export interface Props {
  jobId?: number;
  jobDate?: string;
  customerName?: string;
  customerCode?: string;
  status?: string;
  pickingStatus?: JobPickingStatus;
  bookedBy?: string;
  stopsCount?: number;
  driverCode?: number;
  showCloseBtn?: boolean;
  disabled?: boolean;
  scheduleBoard?: string | null;
  canSchedule?: boolean;
  canScheduleLoading?: boolean;
  isLocked?: boolean;
  lockedBy?: string | null;
  toggleCanSchedule?: (value: boolean) => Promise<void>;
}

const props = withDefaults(defineProps<Props>(), {
  jobId: undefined,
  jobDate: undefined,
  customerName: undefined,
  customerCode: undefined,
  status: undefined,
  pickingStatus: undefined,
  bookedBy: undefined,
  stopsCount: 2,
  driverCode: undefined,
  showCloseBtn: true,
  disabled: false,
  scheduleBoard: null,
  canSchedule: false,
  canScheduleLoading: false,
  isLocked: false,
  lockedBy: null,
  toggleCanSchedule: undefined,
});

const emit = defineEmits<{
  closeClick: [event: Event];
}>();

function handleCloseClick(event: Event): void {
  emit('closeClick', event);
}
</script>

<template>
  <header :class="$style.jobEditPageHeader" data-testid="jobEditPageHeader">
    <div :class="$style.leftSection">
      <KTooltip location="bottom">
        <template #activator="{ props: tooltipProps }">
          <span v-bind="tooltipProps">
            <v-icon :class="$style.lockIcon" size="small">{{
              props.isLocked ? 'mdi-lock' : 'mdi-lock-open'
            }}</v-icon>
          </span>
        </template>
        {{
          props.isLocked
            ? `Job locked by ${props.lockedBy ?? 'unknown'}`
            : 'Job is not locked'
        }}
      </KTooltip>
      <span :class="$style.jobInfo">
        Job #<EmDashPlaceholder :item="props.jobId" /> /
        <EmDashPlaceholder :item="props.jobDate" />
      </span>
      <PickingStatusChip
        v-if="props.pickingStatus"
        :status="props.pickingStatus"
      />
    </div>

    <div :class="$style.centerSection">
      <div :class="$style.infoItem">
        <v-icon :class="$style.icon" size="small">mdi-domain</v-icon>
        <span :class="$style.infoText">
          <EmDashPlaceholder :item="props.customerCode" />
        </span>
      </div>
      <div :class="$style.infoItem">
        <v-icon :class="$style.icon" size="small">mdi-clock-outline</v-icon>
        <span :class="$style.infoText">
          <EmDashPlaceholder :item="props.status" />
        </span>
      </div>
      <div :class="$style.infoItem">
        <v-icon :class="$style.icon" size="small">mdi-account</v-icon>
        <span :class="$style.infoText">
          <EmDashPlaceholder :item="props.bookedBy" />
        </span>
      </div>
    </div>

    <div :class="$style.rightSection">
      <KBtn
        v-if="props.scheduleBoard"
        variant="outlined"
        prepend-icon="mdi-calendar-check"
        disabled
      >
        {{ props.scheduleBoard }}
      </KBtn>
      <KBtn
        v-else-if="props.canSchedule"
        variant="outlined"
        prepend-icon="mdi-check"
        disabled
      >
        CAN SCHEDULE
      </KBtn>
      <KBtn
        v-else
        color="primary"
        prepend-icon="mdi-calendar-check"
        :disabled="props.disabled || props.canScheduleLoading"
        :loading="props.canScheduleLoading"
        @click="props.toggleCanSchedule?.(true)"
      >
        CAN SCHEDULE
      </KBtn>
      <JobEditActionsVMenu
        :disabled="props.disabled"
        :stops-count="props.stopsCount"
        :job-id="props.jobId"
        :job-date="props.jobDate"
        :driver-code="props.driverCode"
        :can-schedule="props.canSchedule"
        :can-schedule-loading="props.canScheduleLoading"
        :toggle-can-schedule="props.toggleCanSchedule"
      />
      <CloseWindowIconBtn v-if="props.showCloseBtn" @click="handleCloseClick" />
    </div>
  </header>
</template>

<style module lang="scss">
@use '@/styles/colors';

.jobEditPageHeader {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background-color: colors.$color-gray-50;
  border-bottom: 1px solid colors.$color-gray-200;
  gap: 24px;
  position: sticky;
  top: 0;
  z-index: 200;
}

.leftSection {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.lockIcon {
  color: colors.$color-text-secondary;
}

.jobInfo {
  font-size: 16px;
  font-weight: 500;
  color: colors.$color-text-primary;
  line-height: 24px;
}

.centerSection {
  display: flex;
  align-items: center;
  gap: 24px;
  flex: 1;
  justify-content: center;
}

.infoItem {
  display: flex;
  align-items: center;
  gap: 8px;
}

.icon {
  color: colors.$color-brand-600;
}

.infoText {
  font-size: 14px;
  color: colors.$color-text-secondary;
  line-height: 20px;
}

.rightSection {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}
</style>
